import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export const COLOR_MODE_STORAGE_KEY = "palmont-color-mode";

/** Chaves antigas por módulo — usadas só na 1ª leitura para migrar a preferência. */
const LEGACY_COLOR_MODE_KEYS = [
  "cadastros-mui-color-mode",
  "controladoria-mui-color-mode",
  "newhome-v2-color-mode",
];

const ColorModeContext = createContext({
  mode: "light",
  toggleColorMode: () => {},
  setMode: () => {},
});

const isValidMode = (value) => value === "dark" || value === "light";

const readStoredMode = () => {
  try {
    const saved = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (isValidMode(saved)) return saved;

    for (const key of LEGACY_COLOR_MODE_KEYS) {
      const legacy = localStorage.getItem(key);
      if (isValidMode(legacy)) return legacy;
    }
  } catch {
    /* ignore */
  }
  return "light";
};

/**
 * Preferência global de tema claro/escuro.
 * Os módulos ainda podem aplicar ThemeProvider/tokens próprios com base em `mode`.
 */
export const ColorModeProvider = ({ children }) => {
  const [mode, setMode] = useState(readStoredMode);

  useEffect(() => {
    try {
      localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
      LEGACY_COLOR_MODE_KEYS.forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch {
      /* ignore */
    }
  }, [mode]);

  const toggleColorMode = useCallback(() => {
    setMode((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  const value = useMemo(
    () => ({ mode, toggleColorMode, setMode }),
    [mode, toggleColorMode]
  );

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);

export default ColorModeProvider;
